import React, { useState, useEffect, createContext, useContext } from 'react';
import axios from 'axios';
import MaintenancePage from '../pages/MaintenancePage';

import { API } from '../config/api';

// Create context for maintenance mode
const MaintenanceContext = createContext({
  isMaintenanceMode: false,
  isLoading: true,
  launchDate: null,
  checkMaintenanceStatus: () => {}
});

export const useMaintenanceMode = () => useContext(MaintenanceContext);

export const MaintenanceProvider = ({ children }) => {
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false);
  const [launchDate, setLaunchDate] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [hasBypassToken, setHasBypassToken] = useState(false);

  const checkMaintenanceStatus = async () => {
    try {
      // Add timeout to prevent infinite waiting
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000); // 3 second timeout
      
      const response = await axios.get(`${API}/maintenance/status`, {
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      
      setIsMaintenanceMode(response.data.maintenance_mode);
      setLaunchDate(response.data.launch_date);
    } catch (error) {
      // On error or timeout, assume not in maintenance mode and allow access
      console.log('Maintenance check skipped (timeout or error)');
      setIsMaintenanceMode(false);
      setLaunchDate(null);
    } finally {
      setIsLoading(false);
    }
  };

  const checkUser = () => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch {
        setUser(null);
      }
    } else {
      setUser(null);
    }
  };

  const checkBypassToken = () => {
    const bypassToken = localStorage.getItem('maintenance_bypass');
    setHasBypassToken(!!bypassToken);
  };

  useEffect(() => {
    checkMaintenanceStatus();
    checkUser();
    checkBypassToken();
    
    // Listen for user updates
    window.addEventListener('storage', checkUser);
    window.addEventListener('userUpdated', checkUser);
    window.addEventListener('storage', checkBypassToken);
    
    // Check maintenance status periodically (every 5 minutes)
    const interval = setInterval(checkMaintenanceStatus, 5 * 60 * 1000);
    
    return () => {
      window.removeEventListener('storage', checkUser);
      window.removeEventListener('userUpdated', checkUser);
      window.removeEventListener('storage', checkBypassToken);
      clearInterval(interval);
    };
  }, []);

  // Re-check when user changes
  useEffect(() => {
    checkUser();
    checkBypassToken();
  }, [isMaintenanceMode]);

  // Determine if we should show maintenance page
  // Allow access if: user is admin, OR has bypass token
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const showMaintenancePage = isMaintenanceMode && !isAdmin && !hasBypassToken;

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <MaintenanceContext.Provider value={{ isMaintenanceMode, isLoading, launchDate, checkMaintenanceStatus }}>
      {showMaintenancePage ? <MaintenancePage launchDate={launchDate} /> : children}
    </MaintenanceContext.Provider>
  );
};

export default MaintenanceProvider;
